/**
 * Wizard Step 5 — Estate Documents.
 *
 * Four tri-state cards (have / create-via-LegalZoom / decline). The
 * "Help me create" pill is only rendered when the LegalZoom affiliate
 * program is active (flag in constants/affiliateLinks.ts); otherwise
 * users see a two-option card.
 *
 * Defaults all four to 'has' = false and 'create' = false (i.e.
 * decline) so continuing without touching the screen is a full no.
 */
import React, { useEffect, useState } from 'react';
import { View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { WizardShell } from '../components/WizardShell';
import { WizardDocumentCard, DocumentCardAnswer } from '../components/WizardDocumentCard';
import { useWizard } from '../WizardContext';
import { analytics } from '../../../services/analytics/analytics';
import { AnalyticsEvent } from '../../../services/analytics/events';

const WIZARD_STEP = 5;
const TOTAL_STEPS = 6;

const toAnswer = (a: { hasDocument: boolean; wantsToCreate?: boolean }): DocumentCardAnswer => {
  if (a.hasDocument) return 'has';
  if (a.wantsToCreate) return 'create';
  return 'decline';
};

const fromAnswer = (a: DocumentCardAnswer): { hasDocument: boolean; wantsToCreate?: boolean } => ({
  hasDocument: a === 'has',
  wantsToCreate: a === 'create',
});

export const WizardDocumentsScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const { answers, setDocuments } = useWizard();

  const [will, setWill] = useState<DocumentCardAnswer>(toAnswer(answers.documents.will));
  const [trust, setTrust] = useState<DocumentCardAnswer>(toAnswer(answers.documents.trust));
  const [advanceDirective, setAdvanceDirective] = useState<DocumentCardAnswer>(
    toAnswer(answers.documents.advanceDirective),
  );
  const [powerOfAttorney, setPowerOfAttorney] = useState<DocumentCardAnswer>(
    toAnswer(answers.documents.powerOfAttorney),
  );

  useEffect(() => {
    analytics.track(AnalyticsEvent.WizardStepShown, { step: 'documents', number: WIZARD_STEP });
  }, []);

  const handleContinue = () => {
    setDocuments({
      will: fromAnswer(will),
      trust: fromAnswer(trust),
      advanceDirective: fromAnswer(advanceDirective),
      powerOfAttorney: fromAnswer(powerOfAttorney),
    });
    analytics.track(AnalyticsEvent.WizardStepCompleted, {
      step: 'documents',
      will,
      trust,
      advance_directive: advanceDirective,
      power_of_attorney: powerOfAttorney,
    });
    navigation.navigate('WizardSuccessorSoftAsk');
  };

  return (
    <WizardShell
      stepNumber={WIZARD_STEP}
      totalSteps={TOTAL_STEPS}
      title="Estate documents"
      subtitle="For each one: do you have it, want help creating it, or not right now?"
      primaryLabel="Continue"
      onPrimaryPress={handleContinue}
      onBackPress={() => navigation.goBack()}
    >
      <View>
        <WizardDocumentCard
          title="Last Will & Testament"
          description="Who gets what, and who makes sure it happens"
          answer={will}
          onChange={setWill}
        />
        <WizardDocumentCard
          title="Living Trust"
          description="Avoids probate, keeps things private"
          answer={trust}
          onChange={setTrust}
        />
        <WizardDocumentCard
          title="Advance Directive"
          description="Your medical wishes if you can't speak for yourself"
          answer={advanceDirective}
          onChange={setAdvanceDirective}
        />
        <WizardDocumentCard
          title="Power of Attorney"
          description="Someone to handle finances or legal matters for you"
          answer={powerOfAttorney}
          onChange={setPowerOfAttorney}
        />
      </View>
    </WizardShell>
  );
};
